import { Users } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { useAuthPermissions } from '@/stores/auth.store';
import { useConferenceStore } from '@/stores/conference.store';
import { isConferenceConfigured } from '@/lib/conference/api';

/**
 * Header button that opens the conference. The room itself lives in
 * {@link ConferenceOverlay} at the dashboard root, so this only flips the store —
 * which means the button can sit anywhere without dragging the modal along.
 *
 * Same permission as the control endpoints: starting a conference places real
 * calls, so a role without `messages:edit` never sees the button.
 */
interface ConferenceTriggerProps {
  /** 'icon' for the header rail; 'labelled' for a page's action row. */
  variant?: 'icon' | 'labelled';
}

export function ConferenceTrigger({ variant = 'icon' }: ConferenceTriggerProps) {
  const open = useConferenceStore((s) => s.open);
  const engaged = useConferenceStore((s) => s.isOpen || s.minimized);
  const canStart = useAuthPermissions().includes('messages:edit');

  // While the modal or the mini-window is showing, the button would be a no-op.
  // An environment with calling switched off gets no button at all rather than
  // one that opens a HUD with nothing behind it.
  if (!isConferenceConfigured || !canStart || engaged) return null;

  if (variant === 'labelled') {
    return (
      <Button type="button" size="sm" className="h-9 gap-1.5" onClick={open}>
        <Users className="size-4" />
        Conference
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-9 w-9"
      aria-label="Start a conference call"
      onClick={open}
    >
      <Users className="h-[18px] w-[18px]" />
    </Button>
  );
}
